import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getProactiveRuns, getProactiveRunAgentRuns, getProactiveAgentRunDetail } from '../api'
import { useI18n } from '../i18n'
import { formatInvokedAt } from '../utils/logFormatters'
import { FilterGroup, FiltersRow, Pagination, PageSection } from '../components/ui'
import StructuredDataView from '../components/StructuredDataView'
import '../styles/pages/models.css'
import '../styles/pages/page-common.css'
import '../styles/pages/chats.css'

function RunListItem({ run, selected, onSelect, t }) {
  const runTypeLabel =
    run.run_type === 'followup' ? t('logs.proactive.trigger.silence_followup') : run.run_type || ''
  const time = formatInvokedAt(run.started_at)
  return (
    <button
      type="button"
      className={`chats-list-item ${selected ? 'chats-list-item--active' : ''}`}
      onClick={() => onSelect(run)}
    >
      <div className="chats-list-item-avatar proactive-run-badge">
        {run.agent_runs_count}
      </div>
      <div className="chats-list-item-body">
        <div className="chats-list-item-name">{runTypeLabel}</div>
        <div className="chats-list-item-model">{time ?? ''}</div>
        <div className="chats-list-item-preview">
          {t('logs.proactive.agentRunsCount', { count: run.agent_runs_count })}
        </div>
      </div>
    </button>
  )
}

function AgentRunListItem({ agentRun, selected, onSelect, t }) {
  const time = formatInvokedAt(agentRun.created_at)
  return (
    <button
      type="button"
      className={`chats-list-item ${selected ? 'chats-list-item--active' : ''}`}
      onClick={() => onSelect(agentRun)}
    >
      <div className="chats-list-item-avatar logs-status-badge" data-status={agentRun.status}>
        {agentRun.status?.[0] || '?'}
      </div>
      <div className="chats-list-item-body">
        <div className="chats-list-item-name">{agentRun.fan_username || agentRun.fan_name || `ID ${agentRun.fan_id}`}</div>
        <div className="chats-list-item-model">{agentRun.model_email || t('common.notAvailable')}</div>
        <div className="chats-list-item-time">
          {time ? `${time} · ` : ''}{agentRun.message_sent ? (agentRun.message_sent.slice(0, 40) + (agentRun.message_sent.length > 40 ? '…' : '')) : '—'}
        </div>
      </div>
    </button>
  )
}

export default function ProactiveLogsPage() {
  const { t } = useI18n()
  const { pathname } = useLocation()
  const logsHref = pathname.startsWith('/v2') ? '/v2/logs' : '/old/logs'
  const [runs, setRuns] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedRun, setSelectedRun] = useState(null)
  const [agentRuns, setAgentRuns] = useState([])
  const [loadingAgentRuns, setLoadingAgentRuns] = useState(false)
  const [selectedAgentRun, setSelectedAgentRun] = useState(null)
  const [agentRunDetail, setAgentRunDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [page, setPage] = useState(0)
  const [agentRunPage, setAgentRunPage] = useState(0)
  const [agentRunsTotal, setAgentRunsTotal] = useState(0)
  const [agentRunsFilter, setAgentRunsFilter] = useState('all') // 'all' | 'with_agent_runs'
  const prevRunIdRef = useRef(null)
  const limit = 20
  const agentRunLimit = 20

  useEffect(() => {
    setLoading(true)
    setError(null)
    const offset = page * limit
    getProactiveRuns({ limit, offset, hasAgentRuns: agentRunsFilter === 'with_agent_runs' })
      .then((r) => {
        setRuns(r?.data || [])
        setTotal(r?.total ?? 0)
        setSelectedRun(null)
        setAgentRuns([])
        setSelectedAgentRun(null)
        setAgentRunDetail(null)
      })
      .catch((err) => {
        setRuns([])
        setTotal(0)
        setError(err?.message || t('logs.error'))
      })
      .finally(() => setLoading(false))
  }, [page, agentRunsFilter])

  useEffect(() => {
    if (!selectedRun) {
      setAgentRuns([])
      setAgentRunsTotal(0)
      setAgentRunPage(0)
      setSelectedAgentRun(null)
      setAgentRunDetail(null)
      prevRunIdRef.current = null
      return
    }
    setAgentRunPage(0)
  }, [selectedRun])

  useEffect(() => {
    if (!selectedRun) return
    setLoadingAgentRuns(true)
    setSelectedAgentRun(null)
    setAgentRunDetail(null)
    const isNewRun = prevRunIdRef.current !== selectedRun.id
    const effectivePage = isNewRun ? 0 : agentRunPage
    prevRunIdRef.current = selectedRun.id
    const offset = effectivePage * agentRunLimit
    getProactiveRunAgentRuns(selectedRun.id, { limit: agentRunLimit, offset })
      .then((r) => {
        setAgentRuns(r?.data || [])
        setAgentRunsTotal(r?.total ?? 0)
      })
      .catch(() => {
        setAgentRuns([])
        setAgentRunsTotal(0)
      })
      .finally(() => setLoadingAgentRuns(false))
  }, [selectedRun, agentRunPage])

  useEffect(() => {
    if (!selectedAgentRun) {
      setAgentRunDetail(null)
      setDetailError(null)
      return
    }
    setLoadingDetail(true)
    setDetailError(null)
    getProactiveAgentRunDetail(selectedAgentRun.id)
      .then((r) => setAgentRunDetail(r))
      .catch((err) => setDetailError(err?.message || t('chats.logs.error')))
      .finally(() => setLoadingDetail(false))
  }, [selectedAgentRun])

  const showAgentRunsPanel = Boolean(selectedRun)
  const showDetailPanel = Boolean(selectedAgentRun)
  const rightPanelContent = showDetailPanel ? 'detail' : showAgentRunsPanel ? 'agentRuns' : 'placeholder'

  return (
    <PageSection
      className="chats-page logs-page logs-page--proactive proactive-logs-page"
      title={t('logs.proactive.title')}
      subtitle={
        <>
          {t('logs.proactive.subtitle')}
          {total > 0 && (
            <span className="logs-proactive-stats">
              {' · '}{t('logs.proactive.runsCount', { count: total })}
            </span>
          )}
        </>
      }
      actions={
        <Link to={logsHref} className="chats-link logs-link-to-graphs">
          {t('logs.proactive.toGraphLogs')}
        </Link>
      }
    >
      <FiltersRow>
        <FilterGroup label={t('logs.proactive.filter.label')}>
          <div className="filter-tabs">
            <button
              type="button"
              className={`filter-tab-btn ${agentRunsFilter === 'all' ? 'filter-tab-btn--active' : ''}`}
              onClick={() => {
                setAgentRunsFilter('all')
                setPage(0)
              }}
            >
              {t('logs.proactive.filter.all')}
            </button>
            <button
              type="button"
              className={`filter-tab-btn ${agentRunsFilter === 'with_agent_runs' ? 'filter-tab-btn--active' : ''}`}
              onClick={() => {
                setAgentRunsFilter('with_agent_runs')
                setPage(0)
              }}
            >
              {t('logs.proactive.filter.withAgentRuns')}
            </button>
          </div>
        </FilterGroup>
      </FiltersRow>
      <div className="chats-layout proactive-logs-layout">
        <aside className="chats-sidebar proactive-runs-sidebar">
          <div className="proactive-sidebar-section proactive-runs-section">
            <h3 className="proactive-sidebar-title">{t('logs.proactive.runsList')}</h3>
            <div className="proactive-runs-scroll">
              {loading && runs.length === 0 ? (
                <p className="chats-sidebar-loading">{t('logs.loading')}</p>
              ) : error ? (
                <p className="chats-sidebar-error">{error}</p>
              ) : runs.length === 0 ? (
                <p className="chats-sidebar-empty">{t('logs.proactive.empty')}</p>
              ) : (
                <ul className="chats-list">
                  {runs.map((run) => (
                    <li key={run.id}>
                      <RunListItem
                        run={run}
                        selected={selectedRun?.id === run.id}
                        onSelect={setSelectedRun}
                        t={t}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {total > 0 && (
              <Pagination
                page={page}
                total={total}
                limit={limit}
                onPageChange={setPage}
                loading={loading}
                t={t}
                rangeKey="logs.proactive.pagination.range"
                pageKey="logs.proactive.pagination.page"
              />
            )}
          </div>
        </aside>

        <aside
          className={`chats-sidebar proactive-graphs-sidebar ${
            showAgentRunsPanel ? 'proactive-graphs-sidebar--visible' : ''
          }`}
        >
          <div className="proactive-sidebar-section proactive-graphs-section">
            <h3 className="proactive-sidebar-title">
              {selectedRun
                ? t('logs.proactive.agentRunsOfRun', { count: agentRunsTotal })
                : t('logs.proactive.selectRun')}
            </h3>
            {selectedRun && (
              <>
                <div className="proactive-graphs-scroll">
                  {loadingAgentRuns && agentRuns.length === 0 ? (
                    <p className="chats-sidebar-loading">{t('logs.loading')}</p>
                  ) : agentRuns.length === 0 ? (
                    <p className="chats-sidebar-empty">{t('logs.proactive.noAgentRunsInRun')}</p>
                  ) : (
                    <ul className="chats-list">
                      {agentRuns.map((ar) => (
                        <li key={ar.id}>
                          <AgentRunListItem
                            agentRun={ar}
                            selected={selectedAgentRun?.id === ar.id}
                            onSelect={setSelectedAgentRun}
                            t={t}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {agentRunsTotal > 0 && (
                  <Pagination
                    page={agentRunPage}
                    total={agentRunsTotal}
                    limit={agentRunLimit}
                    onPageChange={setAgentRunPage}
                    loading={loadingAgentRuns}
                    t={t}
                    rangeKey="logs.proactive.pagination.range"
                    pageKey="logs.proactive.pagination.page"
                  />
                )}
              </>
            )}
          </div>
        </aside>

        <main
          className={`chats-main logs-main ${
            rightPanelContent !== 'placeholder' ? 'chats-main--with-log' : ''
          }`}
        >
          {rightPanelContent === 'placeholder' ? (
            <div className="chats-main-placeholder">
              <p>{t('logs.proactive.selectRunHint')}</p>
              <p className="chats-main-placeholder-hint">{t('logs.proactive.selectAgentRunHint')}</p>
            </div>
          ) : rightPanelContent === 'agentRuns' && !selectedAgentRun ? (
            <div className="chats-main-placeholder">
              <p>{t('logs.proactive.selectAgentRunFromList')}</p>
            </div>
          ) : rightPanelContent === 'detail' && selectedAgentRun ? (
            loadingDetail ? (
              <p className="chats-main-loading">{t('chats.logs.loading')}</p>
            ) : detailError ? (
              <p className="chats-sidebar-error">{detailError}</p>
            ) : !agentRunDetail ? (
              <p className="chats-log-muted">{t('chats.logs.empty')}</p>
            ) : (
              <div className="chats-log-panel logs-detail-panel">
                <div className="chats-log-header">
                  <div className="chats-log-title-wrap">
                    <h3 className="chats-log-title">{t('logs.detailTitle')}</h3>
                    <span className="chats-log-subtitle">
                      {selectedAgentRun.fan_username || selectedAgentRun.fan_name || `Fan ${selectedAgentRun.fan_id}`}
                    </span>
                  </div>
                  <div className="chats-log-header-actions">
                    <button
                      type="button"
                      className="chats-log-back"
                      onClick={() => setSelectedAgentRun(null)}
                    >
                      ← {t('logs.proactive.backToAgentRuns')}
                    </button>
                    <button type="button" className="chats-log-close" onClick={() => setSelectedAgentRun(null)}>
                      {t('chats.logs.close')}
                    </button>
                  </div>
                </div>
                <div className="chats-log-content">
                  <section className="chats-log-section">
                    <h4>{t('logs.proactive.agentRun')}</h4>
                    <StructuredDataView value={agentRunDetail.agent_run} compact />
                  </section>
                </div>
              </div>
            )
          ) : null}
        </main>
      </div>
    </PageSection>
  )
}
